import {
  ApolloClient,
  defaultDataIdFromObject,
  InMemoryCache,
  type StoreValue,
  type TypePolicies,
} from '@apollo/client';
import {get} from 'lodash';

import {envIsDev, envIsIntegration} from './config';
import introspectionQueryResultData from './generated/fragmentTypes.json';
import objectTypeNames from './generated/objectTypeNames.json';
import {apolloLink} from './util/apollo';

const possibleTypes = Object.fromEntries(
  introspectionQueryResultData.__schema.types.map(type => [
    type.name,
    type.possibleTypes.map(possibleType => possibleType.name),
  ])
);

// Objects without a cache ID are embedded within their parent cache entry.
// Apollo Client v3 replaces those objects on write, whereas v2 merged their
// fields. Preserve the v2 behavior for the generated set of embedded types.
const typePolicies: TypePolicies = Object.fromEntries(
  objectTypeNames.map(name => [name, {merge: true}])
);

const appendCustomId = (
  defaultId: string | undefined,
  customIdType: string,
  customId: StoreValue | undefined
) => {
  if (customId == null || defaultId == null) {
    return defaultId;
  }
  return `${defaultId},${customIdType}:${customId}`;
};

// Default on; disable with `GQL_NO_QUERY_DEDUP` query param
// e.g., https://wandb.ai/?GQL_NO_QUERY_DEDUP
const shouldEnableQueryDeduplication = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return !urlParams.has('GQL_NO_QUERY_DEDUP');
};

export const parseRunId = (run: any) => atob(run.id).split(':')[2];

function cacheIdFromObject(
  object: Readonly<Record<string, any>>,
  defaultId: string | undefined
): string | undefined {
  if (object == null || defaultId == null) {
    return defaultId;
  }
  switch (object.__typename) {
    case 'LogLine':
      // LogLine IDs are scoped to a run rather than globally unique.
      return undefined;
    case 'Member':
      return appendCustomId(defaultId, 'role', get(object, 'role'));
    case 'OrganizationMember': {
      const adminId = appendCustomId(defaultId, 'admin', get(object, 'admin'));
      return appendCustomId(adminId, 'orgID', get(object, 'orgID'));
    }
    default:
      return defaultId;
  }
}

export const apolloClient = new ApolloClient({
  typeDefs: [],
  resolvers: {},
  link: apolloLink,
  connectToDevTools: envIsDev || envIsIntegration,
  cache: new InMemoryCache({
    possibleTypes,
    typePolicies,
    dataIdFromObject: object =>
      cacheIdFromObject(object, defaultDataIdFromObject(object)),
  }),
  assumeImmutableResults: true,
  queryDeduplication: shouldEnableQueryDeduplication(),
});

// Apollo v3's no-cache fetch policy does not read from or write to the
// InMemoryCache. Every transitional no-cache client call site supplies that
// policy explicitly, so v3 does not need a second client or cache.
// https://www.apollographql.com/docs/react/v3/caching/advanced-topics/#bypassing-the-cache
export const apolloClientNoCache = apolloClient;
